import { useGrid } from "@/hooks/useGrid";
import { useMarginLevelsBySymbol } from "@/hooks/useMarginLevelsBySymbol";
import { useDashboard } from "@/lib/store";
import { ResolutionString } from "@tradingView/charting_library";
import {
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ComposedChart,
  Area,
} from "recharts";
import { usePaneContext } from "../pane/paneContext";
import { useBinancePrices } from "@/hooks/useBinancePrices";
import { useState, useMemo } from "react";
import { Loader } from "../shared/loader";
import numeral from "numeral";
import { PaneSubHeader } from "../pane/paneSubHeader";
import { Toggle } from "@radix-ui/react-toggle";
import { CheckSquare, Square } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { SymbolSelect } from "../shared/SymbolSelect";

export function MarginLevelsChartPaneBody() {
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const color = config.color;
  const [isCumulative, setIsCumulative] = useState(false);
  const { colorsToSymbolMap, setSymbolForColor } = useDashboard((state) => ({
    colorsToSymbolMap: state.colorsToSymbolMap,
    setSymbolForColor: state.setSymbolForColor,
    indicatorsToColorMap: state.indicatorsToColorMap,
    addIndicatorForColor: state.addIndicatorForColor,
  }));

  const { data: prices } = useBinancePrices();
  const nodeSymbol = colorsToSymbolMap[color];
  const { data, isLoading } = useMarginLevelsBySymbol(
    nodeSymbol,
    "marginLevelsWidget",
  );

  const currentPrice = useMemo(
    () => prices?.[nodeSymbol]?.price ?? 0,
    [prices, nodeSymbol],
  );

  const processedData = useMemo(() => {
    if (!data || !currentPrice) {
      return [];
    }

    // Separate UP and DOWN with pre-allocated arrays
    const upSide = new Array(data.length);
    const downSide = new Array(data.length);
    let upIndex = 0;
    let downIndex = 0;

    // Split data into UP and DOWN arrays
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      if (item.side === "UP" && item.price > currentPrice) {
        upSide[upIndex++] = { ...item, cumulativeVolumeDown: undefined };
      } else if (item.side === "DOWN" && item.price < currentPrice) {
        downSide[downIndex++] = { ...item, cumulativeVolumeUp: undefined };
      }
    }

    // Trim arrays to actual size
    const actualUpSide = upSide.slice(0, upIndex);
    const actualDownSide = downSide.slice(0, downIndex);

    // Sort UP side ascending and calculate cumulative
    actualUpSide.sort((a, b) => a.price - b.price);
    let upCumulative = 0;
    for (let i = 0; i < actualUpSide.length; i++) {
      upCumulative += actualUpSide[i].quoteQuantity;
      actualUpSide[i].cumulativeVolumeUp = upCumulative;
    }

    // Sort DOWN side descending and calculate cumulative
    actualDownSide.sort((a, b) => b.price - a.price);
    let downCumulative = 0;
    for (let i = 0; i < actualDownSide.length; i++) {
      downCumulative += actualDownSide[i].quoteQuantity;
      actualDownSide[i].cumulativeVolumeDown = downCumulative;
    }

    // Find max quote quantity from all data
    const maxQuoteQuantity = Math.max(
      ...actualUpSide.map((item) => item.quoteQuantity),
      ...actualDownSide.map((item) => item.quoteQuantity),
    );

    // Combine results with current price marker
    const result = [
      ...actualUpSide,
      {
        timestamp: Date.now(),
        weight: "LOW",
        price: currentPrice,
        side: "CURRENT",
        quoteQuantity: maxQuoteQuantity,
        cumulativeVolumeUp: 0,
        cumulativeVolumeDown: 0,
      },
      ...actualDownSide,
    ];
    // Final sort by price descending
    return result.sort((a, b) => b.price - a.price);
  }, [data, currentPrice]);

  const getColorBySide: Record<string, string> = useMemo(
    () => ({
      UP: "rgb(60, 166, 75)",
      DOWN: "rgb(178, 24, 44)",
      CURRENT: "white",
    }),
    [],
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        No data available for symbol {nodeSymbol}
      </div>
    );
  }

  return (
    <>
      <PaneSubHeader />
      <div className="h-[calc(100%-40px)] w-full p-2">
        <div className="flex items-center justify-between px-2 py-1">
          <div className="flex items-center">
            <Toggle
              aria-label="Toggle cumulative"
              className="flex items-center text-xs gap-2"
              pressed={isCumulative}
              onPressedChange={setIsCumulative}
            >
              {isCumulative ? (
                <CheckSquare className="h-4 w-4 text-white" />
              ) : (
                <Square className="h-4 w-4 text-white/40" />
              )}
              Show cumulative
            </Toggle>
          </div>
          <div className="flex h-full items-center">
            {nodeSymbol && (
              <div className="flex h-full items-center">
                <label className="flex items-center gap-1 cursor-pointer">
                  <Popover>
                    <PopoverTrigger asChild>
                      <div className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:opacity-50">
                        {nodeSymbol || "Select Symbol"}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[200px]" align="start">
                      <SymbolSelect
                        value={nodeSymbol}
                        onValueChange={(value) => {
                          setSymbolForColor(color, value);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </label>
              </div>
            )}
          </div>
        </div>

        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            barSize={5}
            data={processedData}
            layout="vertical"
            margin={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <XAxis
              type="number"
              color="white"
              stroke="white"
              tick={{ fontSize: 12, fill: "white" }}
              tickSize={2}
              xAxisId="volume"
              label={{ value: "Volume", position: "bottom", fill: "white" }}
            />
            <XAxis type="number" hide={true} xAxisId="cumulative" />
            <YAxis
              type="category"
              dataKey="price"
              color="white"
              stroke="white"
              width={60}
              tickCount={10}
              tick={{ fontSize: 12, fill: "white" }}
              tickSize={2}
            />
            <Tooltip
              cursor={{
                stroke: "white",
                strokeWidth: 1,
                fill: "rgba(0, 0, 0, 0.8)",
              }}
              contentStyle={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              }}
              labelStyle={{
                color: "white",
                fontSize: "12px",
              }}
              itemStyle={{
                color: "white",
                fontSize: "12px",
              }}
              formatter={(value, name, props) => {
                if (name === "price") {
                  const percentDiff = (
                    ((props.payload.price - currentPrice) / currentPrice) *
                    100
                  ).toFixed(2);
                  return [`${props.payload.price} (${percentDiff}%)`];
                }
                if (name === "quoteQuantity") {
                  return [
                    `Volume: $${numeral(value).format("0,0.00a").toUpperCase()}`,
                  ];
                }
                if (name === "Buy Volume") {
                  return [
                    `Buy Volume: $${numeral(value).format("0,0.00a").toUpperCase()}`,
                  ];
                }
                if (name === "Sell Volume") {
                  return [
                    `Sell Volume: $${numeral(value).format("0,0.00a").toUpperCase()}`,
                  ];
                }
                return [value];
              }}
              labelFormatter={(label) => {
                const percentDiff = (
                  ((label - currentPrice) / currentPrice) *
                  100
                ).toFixed(2);
                return `Price: ${label} (${percentDiff}%)`;
              }}
              isAnimationActive={false}
            />
            <Bar
              dataKey="quoteQuantity"
              isAnimationActive={false}
              xAxisId="volume"
            >
              {processedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColorBySide[entry.side]} />
              ))}
            </Bar>
            {isCumulative && (
              <>
                <Area
                  name="Buy Volume"
                  type="monotone"
                  dataKey="cumulativeVolumeUp"
                  stroke="#4CAF50"
                  fill="#4CAF50"
                  fillOpacity={0.2}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  xAxisId="cumulative"
                />
                <Area
                  name="Sell Volume"
                  type="monotone"
                  dataKey="cumulativeVolumeDown"
                  stroke="#FF5252"
                  fill="#FF5252"
                  fillOpacity={0.2}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  xAxisId="cumulative"
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}
